import { useState } from 'react';
import PropTypes from 'prop-types';
import Router from 'next/router';
import { IconButton, Link, Button } from '@mui/material';
import { Chat } from '@mui/icons-material';

import {
    ConnectWithUsMenuComponent,
    FindASpaceMenuComponent,
    WhyHomiiLifeMenuComponent,
} from './desktop-menu-items';

import useDeviceDetect from '../../../utils/device-detect';
import { themeColors } from '../../../utils/theme';
import { eventService } from '../../services/homii-services';

export const MENU_ITEMS = Object.freeze({
    find_a_space: 'find-a-space',
    why_homii_life: 'why_homii_life',
    connect_with_us: 'connect-with-us',
});

const DesktopNavbar = ({ isBookingsPage, isRoomPage }) => {    
    const { isTouchDevice } = useDeviceDetect();
    const [anchorEl, setAnchorEl] = useState(null);
    const [backdropVisible, setBackdropVisible] = useState(false);
    const [currentBackdropData, setCurrentBackdropData] = useState(null);    

    const handleChatMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleFindSpaceClick = () => {
        Router.push('/find-a-space');
    };

    const handleWhyHomiiClick = () => {
        Router.push('/live/live-your-best-life');
    };

    const handleOclubClick = () => {
        Router.push('/o-club');
    };

    const handleConnectClick = () => {
        Router.push('/connect-with-us');
    };

    const onBookNowClick = () => {
        Router.push('/bookings').then(() => window.scrollTo(0, 0));
    };

    const handleMenuItemHover = (type) => {
        setCurrentBackdropData(type);
        setBackdropVisible(true);
        document.body.style.overflow = 'hidden';
    };

    const handleMenuItemHoverEnd = () => {
        setBackdropVisible(false);
        document.body.style.overflow = 'unset';
    };

    const renderBookNowButton = () => {
        
        if (isBookingsPage || isRoomPage) {
            return null;
        }

        return (
            <Button
                className='desktop-nav-book-now-button'
                type="button"
                variant="contained"
                id="desktop-book-now-button"
                onClick={onBookNowClick}
            >
                Book Now
            </Button>
        );
    };

    const handleMenuClose = (type) => {
        if (type === 'whatsapp') {
            eventService.logCustomEvent('whatsapp_contact_us', {
                description: 'Clicked on whatsapp from Contact Us page',
            });
        } else if (type === 'phone') {
            eventService.logCustomEvent('click_to_call_contact_us', {
                description: 'Clicked on phone  from Contact Us page',
            });
        }

        setAnchorEl(null);
    };    

    const renderChatMenu = () => {        
        return (
            <>
                <div role="presentation" className="chat-popover-root chat-container"
                     id="primary-search-account-menu"
                     onClick={() => handleMenuClose()}>
                    <div aria-hidden="true" className='chat-hidden'></div>
                    <div tabIndex="0" data-test="sentinelStart"></div>
                    <div className="chat-paper-root chat-menu-paper chat-popover-paper chat-paper-elevation0 chat-paper-rounded chat-open"
                        tabIndex="-1">
                        <ul className="chat-list-root chat-list-padding chat-menu-list" role="menu" tabIndex="-1">
                            <a
                                onClick={() => handleMenuClose('whatsapp')}
                                className="chat-button-base-root chat-list-item-root chat-item chat-list-item-gutters chat-list-item-button chat-menu-item-root"
                                tabIndex="0" 
                                aria-disabled="false" 
                                role="menuitem" 
                                id="whatsapp-menu-item"
                                href="https://api.whatsapp.com/send?phone=27662818767" target="_blank" rel="noreferrer">
                                <img
                                    src="/assets/images/whatsapp_icon.png" alt="WhatsApp"
                                    className="chat-whatsapp" />
                                    Open in WhatsApp
                                    <span className="menu-touch-ripple-root"></span>
                            </a>                                    
                            <a
                                onClick={() => handleMenuClose('phone')}
                                className="chat-button-base-root chat-list-item-root chat-item chat-list-item-gutters chat-list-item-button chat-menu-item-root"
                                tabIndex="-1" aria-disabled="false" role="menuitem" id="office-phone-menu-item"
                                href="tel:031 327 6400">
                                <svg
                                    className="chat-svg-icon-root chat-svg-icon-fontSizeSmall chat-phone"
                                    focusable="false" viewBox="0 0 24 24" aria-hidden="true">
                                    <path
                                        d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z">
                                    </path>
                                </svg>031 327 6400
                                <span className="menu-touch-ripple-root"></span>
                            </a>
                        </ul>
                    </div>
                    <div tabIndex="0" data-test="sentinelEnd"></div>
                </div>            
            </>  
        );
    }

    return (

        <div className="desktop-nav-wrapper">
            <div className="desktop-nav-container">
                <header className="desktop-nav-paper-root desktop-nav-header desktop-nav-header-color-primary desktop-nav-header-position-fixed desktop-nav-header-root desktop-nav-paper-elevation4">
                    <div className="desktop-nav-toolbar-root desktop-nav-toolbar-gutters desktop-nav-toolbar-regular">
                        <Link href="/" id="homii-logo-link" aria-label="HOMii Home">
                            <span className="desktop-nav-logo-span">
                                <img src="/assets/images/homii_logo.png" alt="HOMii Logo" className="desktop-nav-logo" />
                            </span>                            
                        </Link>
                        <div className="desktop-nav-section">
                            <Button
                                style={{ color: themeColors.white }}
                                id="desktop-link-find-a-space"
                                className="nav-link"
                                onMouseEnter={() => handleMenuItemHover(MENU_ITEMS.find_a_space)}
                                onClick={!isTouchDevice && handleFindSpaceClick}
                            >
                                Find a Space
                            </Button>
                            <Button
                                style={{ color: themeColors.white }}
                                id="desktop-link-why-homii-life"
                                className="nav-link"
                                onMouseEnter={() => handleMenuItemHover(MENU_ITEMS.why_homii_life)}
                                onClick={!isTouchDevice && handleWhyHomiiClick}
                            >
                                Why HOMii Life?
                            </Button>
                            <Button
                                style={{ color: themeColors.white }}
                                id="desktop-link-connect-with-us"
                                className="nav-link"
                                onMouseEnter={() => handleMenuItemHover(MENU_ITEMS.connect_with_us)}
                                onClick={!isTouchDevice && handleConnectClick}
                            >
                                Connect With Us
                            </Button>
                            <Button
                                style={{ color: themeColors.white }}
                                id="desktop-link-o-club"
                                className="nav-link"
                                onClick={!isTouchDevice && handleOclubClick}
                            >
                                O-Club
                            </Button>
                        </div>

                        <div className="desktop-nav-spacer"></div>

                        <div className="desktop-nav-section">
                            <IconButton
                                className="desktop-nav-chat-button"
                                id="chat-menu-button"
                                aria-label="chat-menu-button"
                                aria-controls="primary-search-account-menu"
                                aria-haspopup
                                onClick={handleChatMenuOpen}>
                                <Chat style={{ color: 'white' }}/>
                            </IconButton>
                        </div>
                        {renderBookNowButton()}
                    </div>

                    
                    <div 
                        className={`desktop-nav-backdrop desktop-nav-backdrop-root ${backdropVisible ? 'desktop-nav-backdrop-open' : 'desktop-nav-backdrop-closed'}`}
                        onClick={handleMenuItemHoverEnd}>

                        <div className="menu-container" onMouseLeave={handleMenuItemHoverEnd}>

                            <div className="menu-grid-container menu-grid-spacing-xs-3">

                                {currentBackdropData === MENU_ITEMS.find_a_space && (
                                    <FindASpaceMenuComponent />
                                )}
                                {currentBackdropData === MENU_ITEMS.why_homii_life && (
                                    <WhyHomiiLifeMenuComponent />
                                )}
                                {currentBackdropData === MENU_ITEMS.connect_with_us && (
                                    <ConnectWithUsMenuComponent />
                                )}

                                <div
                                    className="menu-grid-container menu-grid-spacing-xs-3 menu-grid-item menu-grid-md-12 menu-grid-lg-6">
                                    <div className="menu-grid-item menu-grid-lg-4">
                                        <div>
                                            <img src="/assets/images/live.png" alt="menu" className="menu-image" />
                                            <p className="menu-image-title">Live</p>
                                            <p className="menu-image-subtitle">Learn about our app</p>
                                        </div>
                                    </div>
                                    <div className="menu-grid-item menu-grid-lg-4">
                                        <div>
                                            <img src="/assets/images/work.png" alt="menu" className="menu-image" />
                                            <p className="menu-image-title">Work</p>
                                            <p className="menu-image-subtitle">Co-Work with your HOMii's</p>
                                        </div>
                                    </div>
                                    <div className="menu-grid-item menu-grid-lg-4">
                                        <div>
                                            <img src="/assets/images/play.png" alt="menu" className="menu-image" />
                                            <p className="menu-image-title">Play</p>
                                            <p className="menu-image-subtitle">Own your city</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>                        
                    </div>
                </header>
                { anchorEl && (renderChatMenu())}
            </div>
        </div>
    );
};

export default DesktopNavbar;

DesktopNavbar.propTypes = {
    isBookingsPage: PropTypes.bool,
    isRoomPage: PropTypes.bool,
};
